import { Suspense } from "react";
import { Loader2 } from "lucide-react";
import { Outlet, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { CoachSectionTabs } from "@/components/coach/CoachSectionTabs";
import { isChildItemActive } from "@/config/sidebar-nav";
import { useSidebarNavFeature } from "@/hooks/use-sidebar-nav-features";
import { useIsMobile } from "@/hooks/use-mobile";

const fallback = (
  <div className="flex justify-center py-12">
    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
  </div>
);

type Props = {
  featureId: "teams" | "performance";
};

/**
 * Chrome for a tabbed Coach page: the page title (the sidebar label) and the
 * tab strip above the active tab's body. On phones a tab marked
 * `mobileFullScreen` (the pulse rating flow) renders bare, since it is a
 * full-screen flow of its own.
 */
export default function CoachSectionLayout({ featureId }: Props) {
  const feature = useSidebarNavFeature(featureId);
  const { pathname } = useLocation();
  const { t } = useTranslation("nav");
  const isMobile = useIsMobile();

  const tabs = feature?.children ?? [];
  const siblingUrls = tabs.map((c) => c.url);
  const activeTab = tabs.find((c) =>
    isChildItemActive(pathname, c.url, siblingUrls),
  );

  if (!feature || (isMobile && activeTab?.mobileFullScreen)) {
    return (
      <Suspense fallback={fallback}>
        <Outlet />
      </Suspense>
    );
  }

  return (
    <div className="min-w-0 space-y-6 px-4 sm:px-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="font-display text-2xl font-semibold tracking-tight">
          {t(feature.titleKey)}
        </h1>
        <CoachSectionTabs feature={feature} />
      </div>
      <Suspense fallback={fallback}>
        <Outlet />
      </Suspense>
    </div>
  );
}
